import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPHP, formatDate } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty, Stat } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { AlertTriangle, TrendingUp, Users, Activity, X, Scale, GraduationCap, Clock, Trophy } from "lucide-react";
import EmployeePicker from "@/components/app/EmployeePicker";

const LEVEL_BADGE = { high: "badge badge-destructive", medium: "badge badge-pending", low: "badge badge-approved" };

// Plain-English meaning of each risk factor so HR knows what to do about it.
const FACTOR_HINTS = {
    "new hire": "Less than 12 months in — highest churn window. A check-in or onboarding buddy helps.",
    "high lates": "Frequent lateness often precedes disengagement. Worth a 1:1 conversation.",
    "many absences": "Absence pattern above peers — check workload, health, or motivation.",
    "no recent raise": "Pay has not moved recently — review compensation vs the market.",
    "below dept median pay": "Paid under the department median — flight risk to competitors.",
    "low engagement": "Low survey engagement — invite feedback directly.",
    "frequent overtime": "Sustained OT load — burnout risk; review staffing.",
};

/* Drill-down: one employee's risk, factor by factor, with context + jump links. */
function RiskDetail({ item, onClose }) {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    useEffect(() => {
        if (item) api.get(`/hris/employees/${item.employee_id}`).then((r) => setProfile(r.data)).catch(() => {});
    }, [item]);
    if (!item) return null;
    const hint = (f) => FACTOR_HINTS[Object.keys(FACTOR_HINTS).find((k) => f.toLowerCase().includes(k))] || "Contributing signal from this employee's records.";
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                        <AlertTriangle className={`size-4 ${item.level === "high" ? "text-destructive" : item.level === "medium" ? "text-amber-500" : "text-emerald-500"}`} />
                        {item.name} — attrition risk <span className={LEVEL_BADGE[item.level]}>{item.level} · {item.score}</span>
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-auto pr-1">
                    <Surface className="p-3 text-sm">
                        <div className="grid grid-cols-2 gap-y-1 text-xs">
                            <span className="text-muted-foreground">Employee No.</span><span className="font-mono text-right">{item.employee_no}</span>
                            <span className="text-muted-foreground">Position</span><span className="text-right">{item.position || profile?.position || "—"}</span>
                            <span className="text-muted-foreground">Department</span><span className="text-right">{profile?.department_name || profile?.department || "—"}</span>
                            <span className="text-muted-foreground">Tenure</span><span className="text-right">{item.tenure_months != null ? `${item.tenure_months} months` : "—"}</span>
                            <span className="text-muted-foreground">Hire date</span><span className="text-right font-mono">{profile?.hire_date ? formatDate(profile.hire_date) : "—"}</span>
                            <span className="text-muted-foreground">Employment type</span><span className="text-right capitalize">{profile?.employment_type || "—"}</span>
                        </div>
                    </Surface>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Why this score — {item.factors.length || "no"} signal{item.factors.length === 1 ? "" : "s"}</div>
                        <div className="space-y-1.5">
                            {item.factors.map((f, i) => (
                                <div key={i} className="border border-border rounded-md p-2.5">
                                    <div className="text-sm font-medium">{f}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{hint(f)}</div>
                                </div>
                            ))}
                            {!item.factors.length && <div className="text-xs text-muted-foreground border border-border rounded-md p-3">No risk signals — this employee looks stable.</div>}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <div className="flex gap-2">
                        <button className="h-9 px-3 rounded-md border border-border text-sm" onClick={() => navigate(`/timekeeping/attendance`)}>Attendance</button>
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={() => navigate(`/hris/employees/${item.employee_id}`)} data-testid="risk-open-profile">Open profile →</button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* Promotion comparison — two candidates side by side: trainings, attendance
 * discipline, rewards & recognition, latest performance rating. */
function PromotionCompare() {
    const [a, setA] = useState("");
    const [b, setB] = useState("");
    const [da, setDa] = useState(null);
    const [db_, setDb] = useState(null);
    const [loading, setLoading] = useState(false);

    const compare = async () => {
        if (!a || !b) return;
        setLoading(true);
        try {
            const [ra, rb] = await Promise.all([
                api.get(`/analytics/dossier/${a}`), api.get(`/analytics/dossier/${b}`)]);
            setDa(ra.data); setDb(rb.data);
        } catch { setDa(null); setDb(null); }
        setLoading(false);
    };

    // higher-is-better comparison cell with a winner highlight
    const Row = ({ label, va, vb, fmt = (v) => v ?? "—", higherWins = true, icon: Icon }) => {
        const na = typeof va === "number" ? va : null;
        const nb = typeof vb === "number" ? vb : null;
        let winA = false, winB = false;
        if (na != null && nb != null && na !== nb) {
            winA = higherWins ? na > nb : na < nb;
            winB = !winA;
        }
        const cell = (v, win) => (
            <td className={`num font-mono ${win ? "bg-emerald-50 text-emerald-800 font-bold" : ""}`}>{fmt(v)}{win ? " ✓" : ""}</td>
        );
        return (
            <tr>
                <td className="text-xs text-muted-foreground">{Icon && <Icon className="size-3 inline mr-1" />}{label}</td>
                {cell(va, winA)}{cell(vb, winB)}
            </tr>
        );
    };

    return (
        <Surface className="p-4 mt-4" data-testid="promotion-compare">
            <div className="flex items-center gap-2 mb-2">
                <Scale className="size-4 text-primary" />
                <h3 className="font-display font-semibold">Promotion Comparison</h3>
                <span className="text-xs text-muted-foreground">— pick two candidates (e.g., supervisors next in line) and compare their record</span>
            </div>
            <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto] items-end mb-3">
                <div><div className="text-xs text-muted-foreground mb-1">Candidate A</div><EmployeePicker value={a} onChange={setA} testId="cmp-a" /></div>
                <div><div className="text-xs text-muted-foreground mb-1">Candidate B</div><EmployeePicker value={b} onChange={setB} testId="cmp-b" /></div>
                <button className="h-10 px-4 rounded-md bg-primary text-white text-sm font-medium" onClick={compare} disabled={!a || !b || loading} data-testid="cmp-go">{loading ? "Comparing…" : "Compare"}</button>
            </div>

            {da && db_ && (
                <div className="overflow-auto">
                    <table className="w-full dense-table">
                        <thead>
                            <tr>
                                <th className="w-[36%]">Metric</th>
                                <th className="num">{da.employee.name} <span className="font-normal text-muted-foreground">({da.employee.position || "—"})</span></th>
                                <th className="num">{db_.employee.name} <span className="font-normal text-muted-foreground">({db_.employee.position || "—"})</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            <Row label="Tenure (months)" va={da.employee.tenure_months} vb={db_.employee.tenure_months} />
                            <Row icon={GraduationCap} label="Trainings completed" va={da.learning.completed} vb={db_.learning.completed} />
                            <Row icon={GraduationCap} label="Trainings assigned" va={da.learning.assigned} vb={db_.learning.assigned} />
                            <Row icon={GraduationCap} label="Completion rate" va={da.learning.completion_rate} vb={db_.learning.completion_rate} fmt={(v) => v != null ? `${v}%` : "—"} />
                            <Row icon={GraduationCap} label="Avg quiz score" va={da.learning.avg_quiz_score} vb={db_.learning.avg_quiz_score} />
                            <Row icon={GraduationCap} label="Certificates" va={da.learning.certificates} vb={db_.learning.certificates} />
                            <Row icon={Clock} label={`Days present (last ${da.attendance.window_days}d)`} va={da.attendance.days_present} vb={db_.attendance.days_present} />
                            <Row icon={Clock} label="Punctuality" va={da.attendance.punctuality_pct} vb={db_.attendance.punctuality_pct} fmt={(v) => v != null ? `${v}%` : "—"} />
                            <Row icon={Clock} label="Times late" va={da.attendance.late_count} vb={db_.attendance.late_count} higherWins={false} />
                            <Row icon={Clock} label="Total late minutes" va={da.attendance.total_late_minutes} vb={db_.attendance.total_late_minutes} higherWins={false} />
                            <Row icon={Trophy} label="Kudos received" va={da.rewards.kudos_received} vb={db_.rewards.kudos_received} />
                            <Row icon={Trophy} label="Recognition points (total)" va={da.rewards.total_points} vb={db_.rewards.total_points} />
                            <Row icon={Trophy} label="Activity awards" va={da.rewards.activity_awards} vb={db_.rewards.activity_awards} />
                            <Row icon={TrendingUp} label="Latest performance rating" va={da.performance?.rating ?? null} vb={db_.performance?.rating ?? null} />
                        </tbody>
                    </table>
                    <div className="grid md:grid-cols-2 gap-2 mt-3 text-xs text-muted-foreground">
                        <div>
                            <b>{da.employee.name}</b> — top recognition: {da.rewards.top_categories.map(([c, n]) => `${c} ×${n}`).join(", ") || "none"}
                            {da.rewards.award_names?.length > 0 && <> · awards: {da.rewards.award_names.join(", ")}</>}
                        </div>
                        <div>
                            <b>{db_.employee.name}</b> — top recognition: {db_.rewards.top_categories.map(([c, n]) => `${c} ×${n}`).join(", ") || "none"}
                            {db_.rewards.award_names?.length > 0 && <> · awards: {db_.rewards.award_names.join(", ")}</>}
                        </div>
                    </div>
                </div>
            )}
        </Surface>
    );
}

export default function PeopleAnalytics() {
    const [attr, setAttr] = useState(null);
    const [head, setHead] = useState(null);
    const [pay, setPay] = useState(null);
    const [cmp, setCmp] = useState(null);
    const [levelFilter, setLevelFilter] = useState(null); // drill-down from the summary tiles
    const [riskDetail, setRiskDetail] = useState(null);
    const [monthDetail, setMonthDetail] = useState(null);

    useEffect(() => {
        api.get("/analytics/attrition").then((r) => setAttr(r.data)).catch(() => {});
        api.get("/analytics/headcount-forecast").then((r) => setHead(r.data)).catch(() => {});
        api.get("/analytics/payroll-forecast").then((r) => setPay(r.data)).catch(() => {});
        api.get("/analytics/payroll-compare").then((r) => setCmp(r.data)).catch(() => {});
    }, []);

    const cmpData = [
        ...((cmp?.monthly || []).map((m) => ({ month: m.month, net: m.net, kind: m.partial ? "partial" : "actual", mom: m.mom_pct }))),
        ...((cmp?.forecast || []).map((f) => ({ month: f.month + " (f)", net: f.net, kind: "forecast" }))),
    ];
    const CMP_FILL = { actual: "#0F4C81", partial: "#93B7D6", forecast: "#B45309" };

    const headData = (head?.series || []).map((p) => ({
        month: p.month,
        actual: p.kind === "actual" ? p.headcount : null,
        forecast: p.kind === "forecast" ? p.headcount : null,
    }));
    const lastActualIdx = headData.map((p) => p.actual != null).lastIndexOf(true);
    if (lastActualIdx >= 0 && headData[lastActualIdx + 1]) headData[lastActualIdx].forecast = headData[lastActualIdx].actual;

    const payData = (pay?.series || []).map((p) => ({
        period: (p.period || "").slice(2),
        actual: p.kind === "actual" ? p.net : null,
        forecast: p.kind === "forecast" ? p.net : null,
    }));
    const lastPayIdx = payData.map((p) => p.actual != null).lastIndexOf(true);
    if (lastPayIdx >= 0 && payData[lastPayIdx + 1]) payData[lastPayIdx].forecast = payData[lastPayIdx].actual;

    const shownRisk = (attr?.items || []).filter((e) => !levelFilter || e.level === levelFilter);

    return (
        <div>
            <PageHeader title="People Analytics" subtitle="Attrition-risk signals and simple forecasts computed from your own data. Click a tile, a row, or a bar to drill in." />

            {attr && (
                <div className="grid grid-cols-3 gap-3 max-w-xl">
                    {[["high", "High risk", AlertTriangle], ["medium", "Medium risk", Activity], ["low", "Low risk", Users]].map(([lvl, label, Icon]) => (
                        <div key={lvl} className={`cursor-pointer rounded-lg ${levelFilter === lvl ? "ring-2 ring-primary" : ""}`}
                            onClick={() => setLevelFilter(levelFilter === lvl ? null : lvl)} data-testid={`risk-tile-${lvl}`}>
                            <Stat label={label} value={attr.summary[lvl]} icon={Icon} tone={lvl === "high" && attr.summary.high ? "destructive" : undefined} />
                        </div>
                    ))}
                </div>
            )}

            <div className="grid gap-4 lg:grid-cols-2 mt-4">
                <Surface className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display font-semibold">Headcount — last 12 months + 6-month projection</h3>
                        {head && <span className="text-xs text-muted-foreground">{head.slope_per_month >= 0 ? "+" : ""}{head.slope_per_month}/mo trend</span>}
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={headData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#e5e7eb" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="actual" name="Actual" stroke="#0F4C81" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#1E6BB8" strokeWidth={2} strokeDasharray="6 4" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </Surface>

                <Surface className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display font-semibold">Payroll cost (net) — actual + projection</h3>
                        {pay && <span className="text-xs text-muted-foreground">{pay.avg_growth_pct >= 0 ? "+" : ""}{pay.avg_growth_pct}%/period</span>}
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={payData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#e5e7eb" vertical={false} />
                            <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => formatPHP(v)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="actual" name="Actual" stroke="#0F4C81" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#B45309" strokeWidth={2} strokeDasharray="6 4" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </Surface>
            </div>

            {cmp && cmpData.length > 0 && (
                <Surface className="p-4 mt-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <h3 className="font-display font-semibold">Payroll month-over-month & forecast <span className="text-xs font-normal text-muted-foreground">(click a bar)</span></h3>
                        <div className="flex flex-wrap gap-2 text-[11px]">
                            <span className="badge badge-draft">growth {cmp.assumptions.avg_growth_pct >= 0 ? "+" : ""}{cmp.assumptions.avg_growth_pct}%/mo</span>
                            <span className="badge badge-draft">OT trend {formatPHP(cmp.assumptions.ot_trend || 0)}</span>
                            <span className="badge badge-info">+{cmp.assumptions.new_heads || 0} new heads = {formatPHP(cmp.assumptions.headcount_effect || 0)}/mo</span>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={cmpData} margin={{ top: 14, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="#e5e7eb" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
                            <Tooltip formatter={(v, n, pp) => [formatPHP(v), pp?.payload?.kind === "forecast" ? "Forecast" : pp?.payload?.kind === "partial" ? "Partial month" : "Actual"]} />
                            <Bar dataKey="net" radius={[3, 3, 0, 0]} cursor="pointer"
                                onClick={(d) => { const p = d?.payload || d; if (p?.month) setMonthDetail(p); }}>
                                {cmpData.map((d, i) => <Cell key={i} fill={CMP_FILL[d.kind]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 text-[11px] text-muted-foreground mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-sm" style={{ background: CMP_FILL.actual }} /> Actual</span>
                        <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-sm" style={{ background: CMP_FILL.partial }} /> Partial month</span>
                        <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-sm" style={{ background: CMP_FILL.forecast }} /> Forecast (growth + OT trend + new hires)</span>
                        <span className="ml-auto">MoM: {(cmp.monthly || []).filter((m) => m.mom_pct != null).slice(-3).map((m) => m.month.slice(5) + ": " + (m.mom_pct >= 0 ? "+" : "") + m.mom_pct + "%").join(" / ")}</span>
                    </div>
                </Surface>
            )}

            <Surface className="p-4 mt-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <TrendingUp className="size-4 text-primary" /><h3 className="font-display font-semibold">Attrition risk by employee</h3>
                    {levelFilter && (
                        <button className="ml-auto text-xs text-primary flex items-center gap-1" onClick={() => setLevelFilter(null)} data-testid="clear-risk-filter">
                            <X className="size-3" /> {levelFilter} risk only — clear
                        </button>
                    )}
                </div>
                <TableWrap>
                    <thead><tr><th>Employee</th><th>Position</th><th className="num">Tenure</th><th>Risk</th><th className="num">Score</th><th>Why</th></tr></thead>
                    <tbody>
                        {shownRisk.map((e) => (
                            <tr key={e.employee_id} className="cursor-pointer hover:bg-secondary/50" onClick={() => setRiskDetail(e)} data-testid={`risk-row-${e.employee_no}`}>
                                <td><b>{e.name}</b> <span className="text-xs text-muted-foreground">{e.employee_no}</span></td>
                                <td className="text-xs">{e.position || "—"}</td>
                                <td className="num text-xs">{e.tenure_months != null ? `${e.tenure_months} mo` : "—"}</td>
                                <td><span className={LEVEL_BADGE[e.level]}>{e.level}</span></td>
                                <td className="num">
                                    <div className="flex items-center gap-2 justify-end">
                                        <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden"><div className={`h-full ${e.level === "high" ? "bg-destructive" : e.level === "medium" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${e.score}%` }} /></div>
                                        <span className="tabular-nums font-semibold w-6">{e.score}</span>
                                    </div>
                                </td>
                                <td>
                                    <div className="flex flex-wrap gap-1">
                                        {e.factors.length ? e.factors.map((f, i) => <span key={i} className="badge badge-draft">{f}</span>) : <span className="text-xs text-muted-foreground">No risk signals</span>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {attr && !shownRisk.length && <tr><td colSpan={6}><Empty title={levelFilter ? `No ${levelFilter}-risk employees` : "No active employees"} /></td></tr>}
                    </tbody>
                </TableWrap>
            </Surface>

            <PromotionCompare />

            {riskDetail && <RiskDetail item={riskDetail} onClose={() => setRiskDetail(null)} />}

            <Dialog open={!!monthDetail} onOpenChange={(v) => !v && setMonthDetail(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>Payroll — {monthDetail?.month}</DialogTitle></DialogHeader>
                    {monthDetail && (
                        <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Net payroll</span><b className="font-mono">{formatPHP(monthDetail.net)}</b></div>
                            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Kind</span><span className="badge badge-info capitalize">{monthDetail.kind}</span></div>
                            {monthDetail.mom != null && <div className="flex justify-between"><span className="text-muted-foreground text-xs">Month-over-month</span><span className={`font-semibold ${monthDetail.mom >= 0 ? "text-emerald-600" : "text-destructive"}`}>{monthDetail.mom >= 0 ? "+" : ""}{monthDetail.mom}%</span></div>}
                            {monthDetail.kind === "forecast" && cmp?.assumptions && (
                                <div className="text-xs text-muted-foreground border border-border rounded-md p-2 mt-1">
                                    Forecast assumes {cmp.assumptions.avg_growth_pct >= 0 ? "+" : ""}{cmp.assumptions.avg_growth_pct}%/mo growth, {formatPHP(cmp.assumptions.ot_trend || 0)} OT trend, and {formatPHP(cmp.assumptions.headcount_effect || 0)}/mo from {cmp.assumptions.new_heads || 0} new hire(s).
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
